// src/components/demo/DemoScanHome.js
//
// Scan home — the hero (design handoff §02-6/7): carbon ground, orange
// viewfinder brackets, 72px orange SCAN BOARD (the single most-used action —
// orange primary justified here), spot chip top-left, quiet Fleet → link
// top-right, manual-serial fallback under the button.

import React, { useState } from 'react';
import { FScreen, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';
import { FLAVOR_LABEL, IS_DEMO_PREVIEW } from '../../config/flavor';
import DemoScanner from './DemoScanner';
import { skuFromScan } from '../../services/demoService';

// Four orange L-corner brackets (design: DScanHome viewfinder framing).
export const ViewfinderBrackets = ({ size = 180, corner = 44, children }) => (
  <div className="relative mx-auto" style={{ width: size, height: size }}>
    {[[0, 0], [1, 0], [0, 1], [1, 1]].map(([x, y], i) => (
      <div
        key={i}
        style={{
          position: 'absolute', width: corner, height: corner,
          [x ? 'right' : 'left']: 0, [y ? 'bottom' : 'top']: 0,
          borderTop: !y ? '3px solid var(--foam-orange)' : 'none',
          borderBottom: y ? '3px solid var(--foam-orange)' : 'none',
          borderLeft: !x ? '3px solid var(--foam-orange)' : 'none',
          borderRight: x ? '3px solid var(--foam-orange)' : 'none',
        }}
      />
    ))}
    <div className="absolute inset-0 flex items-center justify-center">{children}</div>
  </div>
);

const ScanGlyph = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="var(--foam-paper)" strokeWidth="1.6">
    <rect x="4" y="4" width="7" height="7" /><rect x="13" y="4" width="7" height="7" />
    <rect x="4" y="13" width="7" height="7" /><path d="M13 13h3v3h-3zM17 17h3v3h-3z" />
  </svg>
);

export default function DemoScanHome({
  spot, dayDateLabel, outCount, overdueCount, pendingApprovals,
  canApprove, onSku, onFleet, onChangeSpot, onApprovals, onSignOut, onLead,
}) {
  const [scanning, setScanning] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState('');
  const [manualError, setManualError] = useState(false);

  const submitManual = () => {
    const sku = skuFromScan(manual);
    if (sku) { setManualError(false); onSku(sku); } else { setManualError(true); }
  };

  const topBar = (
    <div className="flex justify-between items-center">
      {/* Spot AND date (2026-08-01 split-fleet incident): the header showed the
          spot only, so two staff on two different demo days had nothing to
          compare. The date is the thing that silently diverged.
          Underlined + "change" so it reads as the switch it always was — this
          is the only place a mis-scoped phone can be spotted mid-day, so it has
          to look actionable, not like a caption (round 2 §2). */}
      <button
        type="button"
        onClick={onChangeSpot}
        className="text-left"
        style={{ minHeight: 44, borderBottom: '1px solid var(--foam-mute)', paddingBottom: 2 }}
      >
        <div className="flex items-center gap-[8px]">
          <FLabel color="var(--foam-paper)">◈ {spot.name}</FLabel>
          <FLabel color="var(--foam-orange)">change</FLabel>
        </div>
        {dayDateLabel && (
          <div className="mt-[2px]">
            <FLabel color="var(--foam-mute)">{dayDateLabel}</FLabel>
          </div>
        )}
      </button>
      <div className="flex items-center gap-[14px]">
        {IS_DEMO_PREVIEW && <FLabel color="var(--foam-orange)">{FLAVOR_LABEL}</FLabel>}
        {canApprove && pendingApprovals > 0 && (
          <button type="button" onClick={onApprovals} style={{ minHeight: 44 }}>
            <FLabel color="var(--foam-orange)">{pendingApprovals} to approve</FLabel>
          </button>
        )}
        <button type="button" onClick={onFleet} style={{ minHeight: 44 }}>
          <FLabel color="var(--foam-mute)">
            Fleet{outCount > 0 ? ` · ${outCount} out` : ''}{overdueCount > 0 ? ' ⚠' : ''} →
          </FLabel>
        </button>
      </div>
    </div>
  );

  return (
    <FScreen
      dark
      header={topBar}
      footer={
        scanning ? (
          <FBtn ghost dark onClick={() => { setScanning(false); setManualOpen(false); }}>Cancel</FBtn>
        ) : (
          <>
            <FBtn primary height={72} className="!text-[18px]" onClick={() => setScanning(true)}>
              Scan board
            </FBtn>
            <button
              type="button"
              onClick={() => { setScanning(true); setManualOpen(true); }}
              className="font-sans text-[12px] text-center w-full"
              style={{ color: 'var(--foam-mute)', minHeight: 44 }}
            >
              No QR? <span style={{ color: 'var(--foam-paper)', textDecoration: 'underline' }}>Enter serial by hand</span>
            </button>
            {onLead && (
              <button
                type="button"
                onClick={onLead}
                className="font-sans text-[12px] text-center w-full"
                style={{ color: 'var(--foam-mute)', minHeight: 44 }}
              >
                Not riding? <span style={{ color: 'var(--foam-paper)', textDecoration: 'underline' }}>Capture a lead</span>
              </button>
            )}
          </>
        )
      }
    >
      {scanning ? (
        <>
          <DemoScanner onSku={onSku} />
          {(
            <div className="flex flex-col gap-[8px]">
              <div className="flex gap-[8px] items-center">
                <input
                  value={manual}
                  onChange={(e) => { setManual(e.target.value); setManualError(false); }}
                  onKeyDown={(e) => e.key === 'Enter' && submitManual()}
                  placeholder="GLIDE-88-V2-A0427"
                  autoCapitalize="characters"
                  autoFocus={manualOpen}
                  className="flex-1 font-mono text-[14px] uppercase px-[12px]"
                  style={{
                    height: 56, border: '1.5px solid var(--foam-carbon-2, #2A2D31)',
                    borderRadius: 0, background: '#FFFFFF', color: 'var(--foam-ink)',
                  }}
                />
                <FBtn dark height={56} className="!w-[80px]" onClick={submitManual}>Go</FBtn>
              </div>
              {manualError && <FLabel color="var(--foam-orange)">That doesn't look like a WAKA serial</FLabel>}
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-[26px]" style={{ minHeight: 320 }}>
          <ViewfinderBrackets><ScanGlyph /></ViewfinderBrackets>
          <div className="text-center">
            <FHeadline size={40} color="var(--foam-paper)">Point at the<br />board's QR</FHeadline>
            <p className="font-sans text-[14px] mt-[12px]" style={{ color: 'var(--foam-mute)' }}>
              We'll do the rest — out or in.
            </p>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="font-mono text-[12px] tracking-[0.14em] uppercase underline"
            style={{ color: 'var(--foam-mute)', minHeight: 44 }}
          >
            Sign out
          </button>
        </div>
      )}
    </FScreen>
  );
}
